import { QueryClient, useMutation as useReactQueryMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { axios } from './axioshttp';
import { useNotification } from '~/shared/notifications/NotificationsContext';
import type { AxiosError } from 'axios';
import type { ProblemDetails } from './types';
import type { DefaultError, UseMutationOptions } from '@tanstack/react-query';
import { useRef } from 'react';

export function mutationOptions<TData = unknown, TError = DefaultError, TVariables = void, TContext = unknown>(
	options: UseMutationOptions<TData, TError, TVariables, TContext>
): UseMutationOptions<TData, TError, TVariables, TContext> {
	return options;
}

export interface useMutationProps<TMutationResult = unknown, TMutationPayload = unknown> {
	url: string;
	method?: string;
	onSuccessMessage?: string;
	onSuccess?: (result: TMutationResult, payload: TMutationPayload, queryClient: QueryClient) => void;
	onError?: (error: AxiosError<ProblemDetails>, payload: TMutationPayload, context: unknown) => void;
	onMutate?: (payload: TMutationPayload) => unknown;
	onSettled?: (result: TMutationResult | undefined, error: AxiosError<ProblemDetails> | null, payload: TMutationPayload, context: unknown) => void;
	transformPayload?: (payload: TMutationPayload) => unknown;
	notifyError?: boolean;
	errorPrompt?: boolean;
	invalidateQueryKeys?: QueryKey[];
	invalidateQueryKeysWithPayload?: (payload: TMutationPayload) => QueryKey[];
}

export function useMutation<TMutationResult = unknown, TMutationPayload = unknown>({
	url,
	method = 'POST',
	onSuccessMessage,
	onSuccess,
	onError,
	onMutate,
	onSettled,
	transformPayload,
	notifyError = true,
	errorPrompt = false,
	invalidateQueryKeys = [],
	invalidateQueryKeysWithPayload
}: useMutationProps<TMutationResult, TMutationPayload>) {
	const { displayToastNotification, displayNotificationDialog } = useNotification();
	const queryClient = useQueryClient();

	// Idempotency: the server dedupes commands by commandId, so the same id must be reused while
	// the same payload is being retried, and a new id issued once the payload changes or a save succeeds.
	const pendingCommand = useRef<{ commandId: string; payloadKey: string } | null>(null);

	const stampCommandId = (processedPayload: unknown) => {
		if (
			processedPayload === null ||
			typeof processedPayload !== 'object' ||
			Array.isArray(processedPayload) ||
			processedPayload instanceof FormData
		) {
			return processedPayload;
		}
		if ('commandId' in processedPayload && processedPayload.commandId) {
			return processedPayload;
		}
		const payloadKey = JSON.stringify(processedPayload);
		if (pendingCommand.current?.payloadKey !== payloadKey) {
			pendingCommand.current = { commandId: crypto.randomUUID(), payloadKey };
		}
		return { ...processedPayload, commandId: pendingCommand.current.commandId };
	};

	const getMutationFunction = (payload: TMutationPayload) => {
		const processedPayload = transformPayload ? transformPayload(payload) : payload;

		switch (method) {
			case 'POST':
				return axios.post(url, processedPayload).then(resp => resp.data);
			case 'PUT':
				return axios.put(url, processedPayload).then(resp => resp.data);
			case 'DELETE':
				return axios.delete(url).then(resp => resp.data);
			case 'UPLOAD':
				return axios
					.post(url, processedPayload, {
						headers: {
							'content-type': 'multipart/form-data'
						}
					})
					.then(resp => resp.data);
			default:
				throw new Error(`Unsupported method: ${method}`);
		}
	};

	return useReactQueryMutation<TMutationResult, AxiosError<ProblemDetails>, TMutationPayload, unknown>({
		mutationFn: getMutationFunction,
		onError: (error, payload, context) => {
			try {
				if (notifyError) {
					const errorMessage = error && error.response && error.response.data.detail ? error.response.data.detail : 'An error occurred';
					if (!errorPrompt) {
						if (error?.response?.data.errors) {
							const errors = Object.values(error.response.data.errors).flat().filter(Boolean).join(', ');
							if (errors) {
								displayToastNotification(errors, 'destructive');
							} else {
								displayToastNotification(errorMessage, 'destructive');
							}
						} else {
							displayToastNotification(errorMessage, 'destructive');
						}
					} else {
						displayNotificationDialog(errorMessage, 'destructive');
					}
					onError?.(error, payload, context);
					return;
				}
				onError?.(error, payload, context);
			} catch (e) {
				console.error('Error handling error', e);
				throw e;
			}
		},
		retry: 0,
		onSuccess: (result, payload, _) => {
			if (onSuccessMessage) {
				displayToastNotification(onSuccessMessage, 'success');
			}
			invalidateQueryKeys.forEach(keys => {
				queryClient.invalidateQueries({ queryKey: keys, refetchType: 'all' });
			});
			if (invalidateQueryKeysWithPayload) {
				const keys = invalidateQueryKeysWithPayload(payload);
				keys.forEach(keys => {
					queryClient.invalidateQueries({ queryKey: keys, refetchType: 'all' });
				});
			}
			if (onSuccess) {
				onSuccess(result, payload, queryClient);
			}
		},
		onSettled,
		onMutate
	});
}
